import { Trash2 } from "lucide-react-native";
import { useCallback, useMemo, useState } from "react";

import ThreadListItemView from "./ThreadListItemView";
import ConfirmDialog from "@/components/molecules/ConfirmDialog";
import { useThreadOperations } from "@/features/knowledge-graph/hooks/useThreadOperations";
import {
  formatThreadLastAccessed,
  generateThreadTitle,
} from "@/features/knowledge-graph/utils/threadUtils";

import type { UserThread } from "@/generated/graphql";
import { useAppLocale } from "@/i18n";

/** Copy shown when the delete mutation comes back unsuccessful. */
const DELETE_FAILED_MESSAGE =
  "Could not delete this conversation. Please try again.";

/**
 * Props for ThreadListItemContainer component
 */
interface ThreadListItemContainerProps {
  thread: UserThread;
  isSelected: boolean;
  onSelect: (threadId: string) => void;
}

/**
 * Container component for displaying a single thread item in the thread list
 * Handles thread selection, title generation, and formatting of thread metadata
 * @param root0 - Component props
 * @param root0.thread - Thread object containing conversation data and metadata
 * @param root0.isSelected - Whether this thread is currently selected/active
 * @param root0.onSelect - Callback invoked when thread is selected, receives thread ID
 */
const ThreadListItemContainer = ({
  thread,
  isSelected,
  onSelect,
}: ThreadListItemContainerProps) => {
  const locale = useAppLocale();
  const { deleteThread } = useThreadOperations();
  const [isConfirmOpen, setIsConfirmOpen] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [deleteError, setDeleteError] = useState<string | null>(null);

  // Handle thread selection
  const handlePress = useCallback(() => {
    onSelect(thread.id);
  }, [onSelect, thread.id]);

  // Memoized thread title. Derived from `firstQuestion` (client-side, via
  // generateThreadTitle) rather than read verbatim from the API's `title`
  // field.
  // @remarks SE-8273: the server-persisted `thread.title` has been observed
  // live in production with special characters (e.g. "%", "€") silently
  // dropped, while `firstQuestion` on the same row is always byte-exact --
  // confirmed by capturing the raw SearchMyThreads GraphQL response, and by
  // tracing every write path in ask-gemini/backend-v2 for a transform that
  // could explain the divergence (none exists in current source on any
  // branch inspected, so the corruption is written upstream of anything
  // this repo -- or the currently deployed backend code -- can fix here).
  // Since `title` is defined as nothing more than a truncated copy of the
  // first question, generating it client-side from the always-intact
  // `firstQuestion` sidesteps the unreliable field entirely. Falls back to
  // the stored `title`, then "New Conversation", for the rare row with no
  // `firstQuestion` -- and also when `firstQuestion` is whitespace-only:
  // `generateThreadTitle` trims such input to an empty string, and a
  // truthy-but-blank `firstQuestion` must not win over those fallbacks.
  const displayTitle = useMemo(
    () =>
      thread.firstQuestion
        ? generateThreadTitle(thread.firstQuestion) ||
          thread.title ||
          "New Conversation"
        : thread.title || "New Conversation",
    [thread.firstQuestion, thread.title]
  );

  // Memoized formatted last accessed time
  const formattedLastAccessed = useMemo(
    () => formatThreadLastAccessed(thread.lastAccessed, locale),
    [thread.lastAccessed, locale]
  );

  // Memoized message count display
  const messageCountText = useMemo(() => {
    const count = thread.messageCount || 0;
    if (count === 0) return "No messages";
    if (count === 1) return "1 message";
    return `${count} messages`;
  }, [thread.messageCount]);

  // Memoized preview text - truncated last answer for better differentiation
  const previewText = useMemo(() => {
    if (!thread.lastAnswer) {
      // Fallback to first question if no answer available
      if (!thread.firstQuestion) return "No preview available";

      const cleaned = thread.firstQuestion
        .replace(/\n/g, " ")
        .replace(/\s+/g, " ")
        .trim();

      return cleaned.length > 60 ? `${cleaned.substring(0, 60)}...` : cleaned;
    }

    // Use last answer as preview to differentiate from title
    const cleaned = thread.lastAnswer
      .replace(/\n/g, " ")
      .replace(/\s+/g, " ")
      .trim();

    return cleaned.length > 80 ? `${cleaned.substring(0, 80)}...` : cleaned;
  }, [thread.lastAnswer, thread.firstQuestion]);

  // SE-8367: the delete affordance. Destructive and irreversible from the
  // user's point of view, so the button opens a confirmation rather than
  // mutating on the first press.
  const handleDelete = useCallback(() => {
    setDeleteError(null);
    setIsConfirmOpen(true);
  }, []);

  const handleCancelDelete = useCallback(() => {
    if (isDeleting) return;
    setIsConfirmOpen(false);
    setDeleteError(null);
  }, [isDeleting]);

  const confirmBody = useMemo(
    () => [
      `Are you sure you want to delete “${displayTitle}”?`,
      "This action cannot be undone.",
    ],
    [displayTitle]
  );

  const handleConfirmDelete = useCallback(async () => {
    setIsDeleting(true);
    setDeleteError(null);

    // `deleteThread` swallows its own errors and reports the outcome as a
    // boolean, so a false here is a real failure, not an absence of one. Keep
    // the dialog open and say so rather than closing on a delete that did not
    // happen.
    const didDelete = await deleteThread(thread.id);

    setIsDeleting(false);

    if (didDelete) {
      setIsConfirmOpen(false);
      return;
    }

    setDeleteError(DELETE_FAILED_MESSAGE);
  }, [deleteThread, thread.id]);

  return (
    <>
      <ThreadListItemView
        title={displayTitle}
        previewText={previewText}
        lastAccessed={formattedLastAccessed}
        messageCount={messageCountText}
        isSelected={isSelected}
        isLoading={isDeleting}
        onPress={handlePress}
        onDelete={handleDelete}
      />

      <ConfirmDialog
        isOpen={isConfirmOpen}
        onClose={handleCancelDelete}
        onConfirm={handleConfirmDelete}
        title="Delete Conversation"
        body={confirmBody}
        icon={Trash2}
        tone="danger"
        confirmLabel={isDeleting ? "Deleting..." : "Delete"}
        isConfirming={isDeleting}
        errorMessage={deleteError ?? undefined}
        testID="delete-thread"
      />
    </>
  );
};

export default ThreadListItemContainer;
